<script setup lang="ts">
import { type Component, computed, ComputedRef, onBeforeMount, Ref, ref } from 'vue'
import GenericTimeSeriesChart from '@/composables/charts/GenericTimeSeriesChart.vue'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import { useInstanceStore } from '@/store/backendInstances'
import TriggerNewBarnDoc from '@/components/incidentTriggers/TriggerNewBarnDoc.vue'
import { humanReadableTimestamp } from '@/composables/datetime'
import { InfluxChartBuilder } from '@/composables/charts/influx-charts/chart-builders/InfluxChartBuilder'

const props = withDefaults(
  defineProps<{
    chartBuilder: InfluxChartBuilder
    enableClickEvent?: boolean
  }>(),
  {
    enableClickEvent: true,
  }
)

const annotationDialogComponent: Ref<Component | null> = ref(null)
const annotationDialogProps: Ref<Record<string, unknown> | null> = ref(null)
const showAnnotationDialog: Ref<boolean> = ref(false)
const annotationFirstTimestamp: Ref<number | undefined> = ref(undefined)
const annotationSecondTimestamp: Ref<number | undefined> = ref(undefined)
const showBarnDocumentationTriggerDialog: Ref<boolean> = ref(false)
const barnDocumentationTriggerStartTimestamp: Ref<number | undefined> = ref(undefined)
const barnDocumentationTriggerEndTimestamp: Ref<number | undefined> = ref(undefined)

const canStartBarnDocAnnotation: ComputedRef<boolean> = computed(() => {
  if (!useInstanceStore().hasAnyPermission(['incidents.barnDocumentation.manage', 'incidents.manage', 'incidents.manage.advanced'])) return false
  if (annotationFirstTimestamp.value) return false
  return true
})
const canEndBarnDocAnnotation: ComputedRef<boolean> = computed(() => {
  return annotationFirstTimestamp.value !== undefined
})

function openAnnotation(annotationComponent: Component, props: Record<string, unknown>) {
  annotationDialogProps.value = props
  annotationDialogComponent.value = annotationComponent
  showAnnotationDialog.value = true
}

function startAnnotation(timestamp: number, close: () => void) {
  close()
  annotationFirstTimestamp.value = timestamp
  props.chartBuilder.startBarnDocAnnotation(timestamp)
}

function finishAnnotation(timestamp: number | undefined, close: (() => void) | undefined) {
  if (close) close()
  annotationSecondTimestamp.value = timestamp
  openBarnDocumentationDialog()
  props.chartBuilder.cancelBarnDocAnnotation()
}

function openBarnDocumentationDialog() {
  barnDocumentationTriggerStartTimestamp.value = Math.min(
    annotationFirstTimestamp.value || Number.MAX_SAFE_INTEGER,
    annotationSecondTimestamp.value || Number.MAX_SAFE_INTEGER
  )
  if (annotationFirstTimestamp.value && annotationSecondTimestamp.value) {
    barnDocumentationTriggerEndTimestamp.value = Math.max(
      annotationFirstTimestamp.value || Number.MIN_SAFE_INTEGER,
      annotationSecondTimestamp.value || Number.MIN_SAFE_INTEGER
    )
  } else {
    barnDocumentationTriggerEndTimestamp.value = undefined
  }
  annotationFirstTimestamp.value = undefined
  annotationSecondTimestamp.value = undefined
  showBarnDocumentationTriggerDialog.value = true
}
function cancelAnnotation() {
  props.chartBuilder.cancelBarnDocAnnotation()
  annotationFirstTimestamp.value = undefined
  annotationSecondTimestamp.value = undefined
}

onBeforeMount(() => {
  props.chartBuilder.attachOpenAnnotationListener(openAnnotation)
})
</script>

<template>
  <generic-custom-dialog v-model="showAnnotationDialog">
    <template #title>Chart Annotation</template>
    <template #body>
      <component
        :is="annotationDialogComponent"
        v-if="annotationDialogComponent"
        v-bind="annotationDialogProps"
      ></component>
    </template>
  </generic-custom-dialog>

  <GenericTimeSeriesChart
    :chart-builder="chartBuilder"
    :enable-click-event="enableClickEvent"
  >
    <template #belowChartColumns>
      <v-row
        v-if="annotationFirstTimestamp"
        justify="center"
        density="comfortable"
      >
        <v-col
          cols="auto"
          class="my-0 py-0"
        >
          <v-btn
            size="small"
            rounded="pill"
            variant="flat"
            color="success"
            prepend-icon="mdi-check"
            @click="finishAnnotation(undefined, undefined)"
          >
            {{ $t('charts.annotations.start', { timestamp: humanReadableTimestamp(annotationFirstTimestamp) }) }}</v-btn
          >
        </v-col>
        <v-col
          cols="auto"
          class="my-0 py-0"
        >
          <v-btn
            size="small"
            variant="outlined"
            color="error"
            prepend-icon="mdi-cancel"
            rounded="pill"
            @click="cancelAnnotation"
          >
            {{ $t('charts.cancel_annotation') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row
        class="justify-end my-2"
        density="comfortable"
      >
        <v-col
          v-for="{ annotator, enabled } in chartBuilder.incidentAnnotators"
          :key="annotator.annotatorId"
          cols="auto"
        >
          <v-btn
            :prepend-icon="enabled ? 'mdi-eye' : 'mdi-eye-off'"
            color="primary"
            class="mx-1"
            size="small"
            rounded="pill"
            :variant="enabled ? 'flat' : 'tonal'"
            @click="chartBuilder.changeAnnotator(annotator.annotatorId, !enabled)"
          >
            {{ annotator.getTitle() }}
          </v-btn>
        </v-col>
      </v-row>
    </template>
    <template #dataPointClickDialogActions="{ timestamp, close }">
      <v-btn
        v-if="canStartBarnDocAnnotation"
        prepend-icon="mdi-bookmark-plus"
        variant="flat"
        color="primary"
        class="my-2"
        block
        @click="startAnnotation(timestamp, close)"
      >
        {{ $t('charts.start_annotation_from_here') }}
      </v-btn>
      <v-btn
        v-if="canEndBarnDocAnnotation"
        class="mt-4 w-100"
        prepend-icon="mdi-bookmark-check"
        variant="flat"
        color="primary"
        @click="finishAnnotation(timestamp, close)"
      >
        {{ $t('charts.finish_annotation_with_timestamp') }}
      </v-btn>
    </template>
  </GenericTimeSeriesChart>
  <generic-custom-dialog v-model="showBarnDocumentationTriggerDialog">
    <template #title>Barn Documentation</template>
    <template #body>
      <TriggerNewBarnDoc
        :pre-filled-first-timestamp="barnDocumentationTriggerStartTimestamp"
        :pre-filled-second-timestamp="barnDocumentationTriggerEndTimestamp"
      ></TriggerNewBarnDoc>
    </template>
  </generic-custom-dialog>
</template>
